import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter,
  Eye,
  MoreVertical,
  Trash2,
  Clock,
  CheckCircle2,
  Wrench,
  AlertTriangle,
  Send,
  ClipboardList,
  Phone,
  Shield,
  X,
  FileText,
  Repeat,
  XCircle,
  Truck,
  ChevronLeft,
  ChevronRight,
  Download,
  Inbox,
  Mail,
  MessageSquare,
  Building2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api, { ordenesAPI, clientesAPI, insuramaAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/usePerformance';
import CrearEtiquetaGLSButton from '@/components/orden/CrearEtiquetaGLSButton';

// Estados en los que el tramitador puede necesitar crear/gestionar etiqueta de envío.
const ESTADOS_ENVIABLES = new Set(['reparado', 'validacion', 'enviado']);

const statusConfig = {
  pendiente_recibir: { label: 'Pendiente Recibir', icon: Clock },
  recibida: { label: 'Recibida', icon: CheckCircle2 },
  cuarentena: { label: 'Cuarentena', icon: AlertTriangle },
  en_taller: { label: 'En Taller', icon: Wrench },
  re_presupuestar: { label: 'Re-presupuestar', icon: AlertTriangle },
  reparado: { label: 'Reparado', icon: CheckCircle2 },
  validacion: { label: 'Validación', icon: ClipboardList },
  enviado: { label: 'Enviado', icon: Send },
  garantia: { label: 'Garantía', icon: Shield },
  cancelado: { label: 'Cancelado', icon: X },
  reemplazo: { label: 'Reemplazo', icon: Repeat },
  irreparable: { label: 'Irreparable', icon: XCircle },
};

export default function Ordenes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [ordenes, setOrdenes] = useState([]);
  const [clientes, setClientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [insuramaInboxCounts, setInsuramaInboxCounts] = useState({});
  const [correosStats, setCorreosStats] = useState({});
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [telefonoBusqueda, setTelefonoBusqueda] = useState('');
  const [autorizacionBusqueda, setAutorizacionBusqueda] = useState('');
  const [estadoFilter, setEstadoFilter] = useState(searchParams.get('estado') || 'all');

  // Filtros de fecha
  const [fechaCampo, setFechaCampo] = useState('created_at'); // created_at | fecha_recibida_centro | fecha_enviado | fecha_fin_reparacion
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [fechaPreset, setFechaPreset] = useState('all'); // all | hoy | 7d | 30d | mes | anio | custom

  // Export Excel
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormato, setExportFormato] = useState('completo'); // completo | unica | resumen
  const [exportandoExcel, setExportandoExcel] = useState(false);
  
  // Paginación
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const PAGE_SIZE = 50;
  
  // Debounce para búsqueda (300ms)
  const debouncedSearch = useDebounce(search, 300);
  const debouncedTelefono = useDebounce(telefonoBusqueda, 300);
  const debouncedAutorizacion = useDebounce(autorizacionBusqueda, 300);
  
  // Estado para el dialog de confirmación de eliminación
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ordenId: null, ordenNumero: '' });
  const [deleting, setDeleting] = useState(false);
  
  // Para detectar escaneo con pistola
  const lastInputTime = useRef(Date.now());
  const inputBuffer = useRef('');
  const scannerTimeoutRef = useRef(null);

  // Función para limpiar el código escaneado
  const cleanScannedCode = (code) => {
    // Eliminar caracteres de control, espacios, tabulaciones, enters, etc.
    return code.replace(/[\x00-\x1F\x7F]/g, '').trim();
  };

  // Detectar si es un escaneo rápido (pistola) vs escritura manual
  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    const now = Date.now();
    const timeDiff = now - lastInputTime.current;
    
    // Si los caracteres llegan muy rápido (< 50ms entre cada uno), es probablemente un escáner
    if (timeDiff < 50 && value.length > 3) {
      inputBuffer.current = value;
      
      // Limpiar timeout anterior
      if (scannerTimeoutRef.current) {
        clearTimeout(scannerTimeoutRef.current);
      }
      
      // Esperar un momento para asegurarnos de que el escaneo terminó
      scannerTimeoutRef.current = setTimeout(async () => {
        const cleanedCode = cleanScannedCode(inputBuffer.current);
        console.log('Código escaneado detectado:', cleanedCode);
        
        // Verificar si es un número de orden (OT-YYYYMMDD-XXXX) o autorización o UUID
        const isOrderNumber = /^OT-\d{8}-[A-F0-9]+$/i.test(cleanedCode);
        const isAuthNumber = /^\d{6,}$/.test(cleanedCode); // Número de autorización
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cleanedCode);
        
        if (isOrderNumber || isAuthNumber || isUUID) {
          // Intentar navegar directamente a la orden
          try {
            const response = await ordenesAPI.obtener(cleanedCode);
            if (response.data && response.data.id) {
              toast.success(`Orden encontrada: ${response.data.numero_orden}`);
              navigate(`/ordenes/${response.data.id}`);
              return;
            }
          } catch (error) {
            // Si no encuentra la orden exacta, buscar normalmente
            console.log('Orden no encontrada directamente, buscando...');
          }
        }
        
        // Si no es una orden exacta, hacer búsqueda normal
        setSearch(cleanedCode);
        inputBuffer.current = '';
      }, 100);
    } else {
      // Escritura manual normal
      setSearch(value);
    }
    
    lastInputTime.current = now;
  }, [navigate]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (scannerTimeoutRef.current) {
        clearTimeout(scannerTimeoutRef.current);
      }
    };
  }, []);

  const fetchOrdenes = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: PAGE_SIZE
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (debouncedTelefono) params.telefono = debouncedTelefono;
      if (debouncedAutorizacion) params.autorizacion = debouncedAutorizacion;
      if (estadoFilter && estadoFilter !== 'all') params.estado = estadoFilter;
      if (fechaCampo && fechaCampo !== 'created_at') params.fecha_campo = fechaCampo;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      
      const [ordenesRes, clientesRes] = await Promise.all([
        ordenesAPI.listarPaginado(params),
        clientesAPI.listar()
      ]);
      
      // El endpoint v2 devuelve { data, total, page, pages }
      setOrdenes(ordenesRes.data.data || []);
      setTotalPages(ordenesRes.data.pages || 1);
      setTotalItems(ordenesRes.data.total || 0);
      
      // Create a map of clients for quick lookup
      const clientesMap = {};
      clientesRes.data.forEach(c => {
        clientesMap[c.id] = c;
      });
      setClientes(clientesMap);
    } catch (error) {
      toast.error('Error al cargar órdenes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Efecto para búsqueda con debounce
  useEffect(() => {
    setPage(1); // Reset a página 1 cuando cambian los filtros
  }, [debouncedSearch, debouncedTelefono, debouncedAutorizacion, estadoFilter, fechaCampo, fechaDesde, fechaHasta]);

  useEffect(() => {
    fetchOrdenes();
  }, [page, debouncedSearch, debouncedTelefono, debouncedAutorizacion, estadoFilter, fechaCampo, fechaDesde, fechaHasta]);

  // Carga conteos del inbox Insurama (una sola llamada cada 2 min)
  useEffect(() => {
    let cancelled = false;
    const fetchInboxCounts = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const { data } = await insuramaAPI.inboxPorOrden();
        if (!cancelled) setInsuramaInboxCounts(data?.por_orden || {});
      } catch {
        // silencio — si falla, no pinta badge
      }
    };
    fetchInboxCounts();
    const interval = setInterval(fetchInboxCounts, 120000);
    const handler = () => fetchInboxCounts();
    window.addEventListener('insurama-inbox-updated', handler);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('insurama-inbox-updated', handler);
    };
  }, []);

  // Carga stats de correos por OT (badge visual)
  useEffect(() => {
    let cancelled = false;
    const fetchCorreos = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const { data } = await api.get('/email/ordenes-stats');
        if (!cancelled) setCorreosStats(data?.ordenes || {});
      } catch {
        // silencio
      }
    };
    fetchCorreos();
    const interval = setInterval(fetchCorreos, 120000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    // La búsqueda ya se ejecuta automáticamente con debounce
  };

  const handleBuscarPorTelefono = (e) => {
    e.preventDefault();
    // La búsqueda ya se ejecuta automáticamente con debounce
  };

  const handleBuscarPorAutorizacion = (e) => {
    e.preventDefault();
    // La búsqueda ya se ejecuta automáticamente con debounce
  };

  const limpiarFiltros = () => {
    setSearch('');
    setTelefonoBusqueda('');
    setAutorizacionBusqueda('');
    setEstadoFilter('all');
    setFechaCampo('created_at');
    setFechaDesde('');
    setFechaHasta('');
    setFechaPreset('all');
    setPage(1);
    setSearchParams({});
  };

  // Aplicar preset de fechas
  const aplicarFechaPreset = (preset) => {
    setFechaPreset(preset);
    const now = new Date();
    const toISO = (d) => d.toISOString().slice(0, 10);
    if (preset === 'all') {
      setFechaDesde('');
      setFechaHasta('');
    } else if (preset === 'hoy') {
      const d = toISO(now);
      setFechaDesde(d);
      setFechaHasta(d);
    } else if (preset === '7d') {
      const desde = new Date(now); desde.setDate(now.getDate() - 6);
      setFechaDesde(toISO(desde));
      setFechaHasta(toISO(now));
    } else if (preset === '30d') {
      const desde = new Date(now); desde.setDate(now.getDate() - 29);
      setFechaDesde(toISO(desde));
      setFechaHasta(toISO(now));
    } else if (preset === 'mes') {
      const desde = new Date(now.getFullYear(), now.getMonth(), 1);
      setFechaDesde(toISO(desde));
      setFechaHasta(toISO(now));
    } else if (preset === 'anio') {
      const desde = new Date(now.getFullYear(), 0, 1);
      setFechaDesde(toISO(desde));
      setFechaHasta(toISO(now));
    }
    // 'custom' → no toca fechas, el usuario escribe a mano
  };

  const handleExportExcel = async () => {
    setExportandoExcel(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (debouncedTelefono) params.append('telefono', debouncedTelefono);
      if (debouncedAutorizacion) params.append('autorizacion', debouncedAutorizacion);
      if (estadoFilter && estadoFilter !== 'all') params.append('estado', estadoFilter);
      if (fechaCampo && fechaCampo !== 'created_at') params.append('fecha_campo', fechaCampo);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      params.append('formato', exportFormato);

      const res = await api.get(`/ordenes-export-excel?${params.toString()}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ordenes_${fechaDesde || 'inicio'}_${fechaHasta || 'hoy'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
      setShowExportDialog(false);
    } catch (err) {
      console.error(err);
      toast.error('Error al exportar Excel');
    } finally {
      setExportandoExcel(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await ordenesAPI.eliminar(id);
      toast.success('Orden eliminada correctamente');
      // Actualizar la lista localmente sin recargar toda la página
      setOrdenes(prev => prev.filter(o => o.id !== id));
    } catch (error) {
      toast.error('Error al eliminar orden');
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, ordenId: null, ordenNumero: '' });
    }
  };

  const openDeleteDialog = (orden) => {
    setDeleteDialog({ 
      open: true, 
      ordenId: orden.id, 
      ordenNumero: orden.numero_orden || orden.numero_autorizacion || orden.id 
    });
  };

  const handleEstadoChange = (value) => {
    setEstadoFilter(value);
    if (value && value !== 'all') {
      setSearchParams({ estado: value });
    } else {
      setSearchParams({});
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Devuelve la fecha+hora en que la orden entró en su estado ACTUAL.
  // Busca la última entrada de historial_estados cuyo `estado` coincide con el estado actual.
  // Fallbacks: fecha_recibida_centro para `recibida`, fecha_fin_reparacion para `reparado`,
  // fecha_enviado para `enviado`, o updated_at.
  const fechaEntradaEstado = (orden) => {
    const historial = Array.isArray(orden.historial_estados) ? orden.historial_estados : [];
    for (let i = historial.length - 1; i >= 0; i--) {
      if (historial[i]?.estado === orden.estado && historial[i]?.fecha) {
        return historial[i].fecha;
      }
    }
    if (orden.estado === 'recibida' && orden.fecha_recibida_centro) return orden.fecha_recibida_centro;
    if (orden.estado === 'reparado' && orden.fecha_fin_reparacion) return orden.fecha_fin_reparacion;
    if (orden.estado === 'enviado' && orden.fecha_enviado) return orden.fecha_enviado;
    return orden.updated_at || null;
  };

  const formatDiaHora = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '—';
    const fecha = d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
    const hora = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return `${fecha} · ${hora}`;
  };

  const tiempoEnEstado = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null;
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) return null;
    const minutos = Math.floor(diffMs / 60000);
    if (minutos < 60) return `${minutos}m`;
    const horas = Math.floor(minutos / 60);
    if (horas < 24) return `${horas}h`;
    const dias = Math.floor(horas / 24);
    return `${dias}d`;
  };

  // Mapa visual del tipo de último contacto (icono + color + label)
  const CONTACTO_META = {
    llamada_cliente: { Icon: Phone, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Llamada' },
    whatsapp: { Icon: MessageSquare, color: 'text-green-600', bg: 'bg-green-100', label: 'WhatsApp' },
    email: { Icon: Mail, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Correo' },
    comunicacion_aseguradora: { Icon: Building2, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Aseguradora' },
  };

  // Devuelve un texto humano: "hace 2d", "hace 5h", "hoy" para mostrar antigüedad del último contacto
  const hace = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null;
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) return null;
    const minutos = Math.floor(diffMs / 60000);
    if (minutos < 5) return 'ahora';
    if (minutos < 60) return `${minutos}m`;
    const horas = Math.floor(minutos / 60);
    if (horas < 24) return `${horas}h`;
    const dias = Math.floor(horas / 24);
    return `${dias}d`;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ordenes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Órdenes de Trabajo</h1>
          <p className="text-muted-foreground mt-1">Gestiona las reparaciones</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowExportDialog(true)}
            data-testid="btn-abrir-export-excel-ordenes"
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Exportar Excel
          </Button>
          <Link to="/ordenes/nueva">
            <Button data-testid="new-order-btn" className="gap-2">
              <Plus className="w-4 h-4" />
              Nueva Orden
            </Button>
          </Link>
        </div>
      </div>

      {/* Filtros — layout compacto de 2 filas */}
      <Card>
        <CardContent className="p-4 space-y-3">
          {/* Fila 1: búsqueda principal + estado + teléfono + autorización */}
          <div className="grid grid-cols-12 gap-2">
            <div className="relative col-span-12 lg:col-span-5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por número, modelo o IMEI… (compatible con escáner)"
                value={search}
                onChange={handleSearchInputChange}
                className="pl-10 h-10"
                data-testid="search-input"
                autoComplete="off"
              />
            </div>
            <div className="col-span-6 lg:col-span-3">
              <Select value={estadoFilter} onValueChange={handleEstadoChange}>
                <SelectTrigger className="h-10" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {Object.entries(statusConfig).map(([key, { label }]) => {
                    if (key === 'validacion') {
                      return <SelectItem key={key} value="validacion,enviado">Validación + Enviado</SelectItem>;
                    }
                    if (key === 'enviado') return null;
                    return <SelectItem key={key} value={key}>{label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="relative col-span-6 lg:col-span-2">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Teléfono…"
                value={telefonoBusqueda}
                onChange={(e) => setTelefonoBusqueda(e.target.value)}
                className="pl-10 h-10"
                data-testid="phone-search-input"
              />
            </div>
            <div className="relative col-span-12 lg:col-span-2">
              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Nº Autorización…"
                value={autorizacionBusqueda}
                onChange={(e) => setAutorizacionBusqueda(e.target.value)}
                className="pl-10 h-10 font-mono"
                data-testid="auth-search-input"
              />
            </div>
          </div>

          {/* Fila 2: filtros de fecha en una sola línea */}
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border" data-testid="filtros-fecha-ordenes">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mr-1">Fechas</span>
            <Select value={fechaCampo} onValueChange={setFechaCampo}>
              <SelectTrigger className="w-44 h-9" data-testid="filtro-fecha-campo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Fecha de creación</SelectItem>
                <SelectItem value="fecha_recibida_centro">Fecha recepción</SelectItem>
                <SelectItem value="fecha_inicio_reparacion">Inicio reparación</SelectItem>
                <SelectItem value="fecha_fin_reparacion">Fin reparación</SelectItem>
                <SelectItem value="fecha_enviado">Fecha envío</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-1 flex-wrap">
              {[
                { id: 'all',  label: 'Todo' },
                { id: 'hoy',  label: 'Hoy' },
                { id: '7d',   label: '7 días' },
                { id: '30d',  label: '30 días' },
                { id: 'mes',  label: 'Mes actual' },
                { id: 'anio', label: 'Año' },
                { id: 'custom', label: 'Custom' },
              ].map(p => (
                <Button
                  key={p.id}
                  type="button"
                  size="sm"
                  variant={fechaPreset === p.id ? 'default' : 'outline'}
                  onClick={() => aplicarFechaPreset(p.id)}
                  data-testid={`filtro-fecha-preset-${p.id}`}
                  className="h-9 text-xs"
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <Input
                type="date"
                value={fechaDesde}
                onChange={(e) => { setFechaDesde(e.target.value); setFechaPreset('custom'); }}
                className="h-9 w-36"
                data-testid="filtro-fecha-desde"
                aria-label="Desde"
              />
              <span className="text-xs text-muted-foreground">→</span>
              <Input
                type="date"
                value={fechaHasta}
                onChange={(e) => { setFechaHasta(e.target.value); setFechaPreset('custom'); }}
                className="h-9 w-36"
                data-testid="filtro-fecha-hasta"
                aria-label="Hasta"
              />
            </div>
            {(search || telefonoBusqueda || autorizacionBusqueda || estadoFilter !== 'all' || fechaDesde || fechaHasta) && (
              <Button variant="ghost" onClick={limpiarFiltros} size="sm" className="h-9">
                <X className="w-4 h-4 mr-1" />
                Limpiar
              </Button>
            )}
            <div className="ml-auto text-sm text-muted-foreground tabular-nums">
              {totalItems} orden{totalItems !== 1 ? 'es' : ''}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              Cargando órdenes...
            </div>
          ) : ordenes.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">No hay órdenes</p>
              <p className="text-muted-foreground">Crea tu primera orden de trabajo</p>
              <Link to="/ordenes/nueva">
                <Button className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Orden
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº Autorización</TableHead>
                    <TableHead>Nº Orden</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Dispositivo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Agencia</TableHead>
                    <TableHead className="w-16 text-center">GLS</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ordenes.map((orden) => {
                    const cliente = clientes[orden.cliente_id];
                    const StatusIcon = statusConfig[orden.estado]?.icon;
                    return (
                      <TableRow
                        key={orden.id}
                        data-testid={`orden-row-${orden.id}`}
                        className="cursor-pointer hover:bg-accent/50"
                        onClick={() => navigate(`/ordenes/${orden.id}`)}
                      >
                        <TableCell>
                          {orden.numero_autorizacion ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="font-mono text-sm font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                                {orden.numero_autorizacion}
                              </span>
                              {insuramaInboxCounts[orden.id] > 0 && (
                                <Badge
                                  className="bg-orange-500 hover:bg-orange-500 text-white text-[10px] h-5 px-1.5 gap-0.5 animate-pulse"
                                  title={`${insuramaInboxCounts[orden.id]} mensaje(s) Insurama sin leer`}
                                  data-testid={`badge-insurama-inbox-${orden.id}`}
                                >
                                  <Inbox className="w-3 h-3" />
                                  {insuramaInboxCounts[orden.id]}
                                </Badge>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="font-mono text-xs text-muted-foreground">
                              {orden.numero_orden}
                            </span>
                            {orden.mensajes_no_leidos > 0 && (
                              <Badge
                                className="text-[10px] h-5 px-1.5 gap-0.5 bg-red-600 hover:bg-red-600 text-white animate-pulse"
                                title={`${orden.mensajes_no_leidos} mensaje(s) interno(s) sin leer`}
                                data-testid={`badge-mensajes-${orden.id}`}
                              >
                                <MessageSquare className="w-3 h-3" />
                                {orden.mensajes_no_leidos}
                              </Badge>
                            )}
                            {correosStats[orden.id] && (correosStats[orden.id].no_leidos > 0 || correosStats[orden.id].esperando_respuesta) ? (
                              <Badge
                                className={`text-[10px] h-5 px-1.5 gap-0.5 ${
                                  correosStats[orden.id].no_leidos > 0
                                    ? 'bg-blue-600 hover:bg-blue-600 text-white animate-pulse'
                                    : 'bg-amber-500 hover:bg-amber-500 text-white'
                                }`}
                                title={
                                  correosStats[orden.id].no_leidos > 0
                                    ? `${correosStats[orden.id].no_leidos} correo(s) sin leer`
                                    : 'Cliente esperando respuesta'
                                }
                                data-testid={`badge-correo-${orden.id}`}
                              >
                                <Mail className="w-3 h-3" />
                                {correosStats[orden.id].no_leidos > 0 ? correosStats[orden.id].no_leidos : '!'}
                              </Badge>
                            ) : correosStats[orden.id]?.total > 0 ? (
                              <Mail
                                className="w-3 h-3 text-muted-foreground"
                                title={`${correosStats[orden.id].total} correo(s) vinculados`}
                              />
                            ) : null}
                            {orden.nsys_disponible && orden.nsys_data?.fetched_at ? (
                              <Badge
                                className="text-[10px] h-5 px-1.5 gap-0.5 bg-teal-100 text-teal-700 border-teal-300 hover:bg-teal-100"
                                title={`NSYS consultado · diagnóstico cacheado (${new Date(orden.nsys_data.fetched_at).toLocaleDateString('es-ES')})`}
                                data-testid={`badge-nsys-${orden.id}`}
                              >
                                NSYS ✓
                              </Badge>
                            ) : orden.nsys_disponible ? (
                              <Badge
                                className="text-[10px] h-5 px-1.5 gap-0.5 bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100 animate-pulse"
                                title="Diagnóstico NSYS disponible — pulsa la OT para descargarlo (consume 1 licencia)"
                                data-testid={`badge-nsys-pending-${orden.id}`}
                              >
                                NSYS · pendiente
                              </Badge>
                            ) : null}
                          </span>
                        </TableCell>
                        <TableCell>
                          {cliente ? (
                            <div>
                              <p className="font-medium">{cliente.nombre} {cliente.apellidos}</p>
                              <p className="text-sm text-muted-foreground">{cliente.telefono}</p>
                              {(() => {
                                const uc = orden.ultimo_contacto;
                                if (!uc?.tipo || !uc?.fecha) return null;
                                const meta = CONTACTO_META[uc.tipo];
                                if (!meta) return null;
                                const Icon = meta.Icon;
                                const tiempo = hace(uc.fecha);
                                const fechaHumana = (() => {
                                  try {
                                    return new Date(uc.fecha).toLocaleString('es-ES', {
                                      day: '2-digit', month: '2-digit', year: '2-digit',
                                      hour: '2-digit', minute: '2-digit',
                                    });
                                  } catch { return uc.fecha; }
                                })();
                                return (
                                  <span
                                    className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.bg} ${meta.color}`}
                                    title={`Último contacto: ${meta.label} · ${fechaHumana}`}
                                    data-testid={`ultimo-contacto-${orden.id}`}
                                  >
                                    <Icon className="w-2.5 h-2.5" />
                                    {meta.label}
                                    {tiempo && (
                                      <span className="ml-0.5 opacity-75">· hace {tiempo}</span>
                                    )}
                                  </span>
                                );
                              })()}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium">{orden.dispositivo?.modelo}</p>
                              {orden.es_garantia && (
                                <Badge
                                  className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px] h-5 px-1.5 gap-0.5 hover:bg-orange-100"
                                  title={orden.numero_orden_padre ? `Orden de garantía de ${orden.numero_orden_padre}` : 'Orden de garantía'}
                                  data-testid={`badge-garantia-${orden.id}`}
                                >
                                  <Shield className="w-3 h-3" />
                                  GARANTÍA
                                </Badge>
                              )}
                            </div>
                            {orden.dispositivo?.imei && (
                              <p className="text-xs font-mono text-muted-foreground">
                                {orden.dispositivo.imei}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge className={`badge-status status-${orden.estado} gap-1 w-fit`}>
                              {StatusIcon && <StatusIcon className="w-3 h-3" />}
                              {statusConfig[orden.estado]?.label}
                            </Badge>
                            {(() => {
                              const f = fechaEntradaEstado(orden);
                              if (!f) return null;
                              const tiempo = tiempoEnEstado(f);
                              return (
                                <div
                                  className="text-[10.5px] leading-tight text-muted-foreground"
                                  data-testid={`orden-estado-desde-${orden.id}`}
                                  title={`Entró en estado "${statusConfig[orden.estado]?.label}" el ${formatDiaHora(f)}`}
                                >
                                  <span className="font-medium text-foreground/70">desde</span> {formatDiaHora(f)}
                                  {tiempo && (
                                    <span className="ml-1 text-muted-foreground/60">({tiempo})</span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{orden.agencia_envio}</p>
                            <p className="text-xs font-mono text-muted-foreground">
                              {orden.codigo_recogida_entrada}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                          <GLSCellIcon orden={orden} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(orden.created_at)}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/ordenes/${orden.id}`}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Ver detalle
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteDialog(orden);
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Mostrando {ordenes.length} de {totalItems} órdenes
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </Button>
                <span className="text-sm px-2">
                  Página {page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmación de eliminación */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, ordenId: null, ordenNumero: '' })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta orden?</AlertDialogTitle>
            <AlertDialogDescription>
              Estás a punto de eliminar la orden <strong>{deleteDialog.ordenNumero}</strong>. 
              Esta acción no se puede deshacer y se perderán todos los datos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteDialog.ordenId)}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Eliminando...' : 'Sí, eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Exportar Excel */}
      {showExportDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => !exportandoExcel && setShowExportDialog(false)}
          data-testid="dialog-export-excel-ordenes"
        >
          <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                <h2 className="text-lg font-semibold">Exportar Órdenes a Excel</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Se exportarán las órdenes que coincidan con los filtros activos. {totalItems} orden{totalItems !== 1 ? 'es' : ''} encontrada{totalItems !== 1 ? 's' : ''}.
              </p>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-1">
                  Periodo
                </label>
                <p className="text-sm">
                  {fechaCampo === 'created_at' ? 'Creación' :
                   fechaCampo === 'fecha_recibida_centro' ? 'Recepción' :
                   fechaCampo === 'fecha_inicio_reparacion' ? 'Inicio reparación' :
                   fechaCampo === 'fecha_fin_reparacion' ? 'Fin reparación' :
                   fechaCampo === 'fecha_enviado' ? 'Envío' : fechaCampo}
                  {' · '}
                  {fechaDesde || 'inicio'} → {fechaHasta || 'hoy'}
                </p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-1">
                  Formato del archivo
                </label>
                <Select value={exportFormato} onValueChange={setExportFormato}>
                  <SelectTrigger data-testid="export-formato-select-ordenes">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completo">Completo (Resumen + Detalle)</SelectItem>
                    <SelectItem value="unica">Sólo Detalle (1 hoja)</SelectItem>
                    <SelectItem value="resumen">Sólo Resumen / KPIs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowExportDialog(false)} disabled={exportandoExcel}>
                  Cancelar
                </Button>
                <Button onClick={handleExportExcel} disabled={exportandoExcel} data-testid="btn-confirmar-export-excel-ordenes">
                  {exportandoExcel ? 'Generando…' : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Descargar
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

/**
 * Icono camión GLS por orden:
 *   - Verde: ya tiene etiqueta creada (enlace al tracking)
 *   - Azul: estado enviable pero sin etiqueta → click abre dialog de creación
 *   - Gris disabled: la orden aún no necesita envío
 */
function GLSCellIcon({ orden }) {
  const envios = Array.isArray(orden.gls_envios) ? orden.gls_envios : [];
  const tiene = envios.length > 0;
  const ultimo = tiene ? envios[envios.length - 1] : null;
  const estadoEnviable = ESTADOS_ENVIABLES.has(orden.estado);

  if (tiene) {
    const url = ultimo?.tracking_url;
    return (
      <a
        href={url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title={`Etiqueta GLS ${ultimo.codbarras}`}
        className="inline-flex items-center justify-center rounded-full p-1.5 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition"
        data-testid={`gls-icon-ok-${orden.id}`}
      >
        <Truck className="w-4 h-4" />
      </a>
    );
  }

  if (!estadoEnviable) {
    return (
      <span title="Aún no en estado de envío"
            className="inline-flex items-center justify-center rounded-full p-1.5 bg-muted text-muted-foreground/50"
            data-testid={`gls-icon-disabled-${orden.id}`}>
        <Truck className="w-4 h-4" />
      </span>
    );
  }

  // Enviable pero sin etiqueta: CTA rápido en la celda (renderiza nuestro botón en modo icono)
  return (
    <div title="Crear etiqueta GLS"
         className="inline-flex"
         data-testid={`gls-icon-pendiente-${orden.id}`}>
      <CrearEtiquetaGLSButton
        orden={orden}
        variant="ghost"
        label=""
      />
    </div>
  );
}
